import type { TEnvironment } from "@/core/environment/types";
import { useAccountStoreShallow } from "@/features/account-management";
import { useGetAccountQuery } from "@/shared/services";
import { environment } from "@environment";
import moment from "moment";
import { useEffect } from "react";
import { useAnalyticsStoreShallow } from "../store";
import { register } from "../utils";

export const useRegisterAccountProperties = (authenticated = false) => {
  const { data: account } = useGetAccountQuery();
  const { planType, subscription, isSubscriptionExpired } =
    useAccountStoreShallow((s) => ({
      planType: s.planType,
      subscription: s.subscription,
      isSubscriptionExpired: s.isSubscriptionExpired,
    }));
  const { setInitializedType } = useAnalyticsStoreShallow((s) => ({
    setInitializedType: s.setInitializedType,
  }));

  useEffect(() => {
    if (!authenticated || !account) return;

    const isFreeTrial = planType === "trial";
    const {
      customization: accountCustomization,
      parameters: accountParameters,
      timestamps: accountTimestamps,
      wizardPayload: accountWizardPayload,
    } = account;

    const customization =
      accountCustomization ?? ({} as Record<string, unknown>);
    const parameters = accountParameters ?? {};
    const timestamps = accountTimestamps ?? {};
    const wizardPayload = accountWizardPayload ?? {};
    const wizardEnvironment = parameters.wizardEnvironment;
    const firstPlan =
      environment.paddle.plans.find(
        (plan) => plan.id === account.firstSubscriptionPlanId,
      ) || ({} as TEnvironment["paddle"]["plans"][number]);

    const data = {
      "organization id": account._id,
      "organization name": account.title,
      "organization created": account.createdAt,
      "organization free trial": isFreeTrial,
      "organization is self serv": account.isSelfServ,
      "organization type": account.isSelfServ ? "Self serve" : "B2B",
      "organization verified": !!account.verifiedAt,
      "user is self serv": account.isSelfServ,
      "first wizard website": account.website,
      "testing number": account.testingNumber,
      "editor version": "4",
      "coupon code": account.paymentCoupon,
      "organization has profile logo": !!customization.logo,
      "organization has profile favicon": !!account.favicon,
      "organization first analytics": timestamps.firstAnalytics,
      "first wizard environment": wizardEnvironment,
      "first wizard subtype": wizardPayload["subtype select"],
      "first wizard subtype category": wizardPayload["subtype category"],
      "first template structure version":
        wizardPayload["template structure version"],
      "first signup flow": wizardPayload["first signup flow"],
      "user source": wizardPayload["user source"],
      "wizard business type": wizardPayload["wizard business type"],
      "wizard business size": wizardPayload["wizard business size"],
      "wizard industry": wizardPayload["wizard industry"],
      "wizard role name": wizardPayload["wizard role name"],
      "wizard crm system": wizardPayload["wizard crm system"],
      "wizard collaboration type": wizardPayload["wizard collaboration type"],
      "organization enabled automation": !!account.jwtTokenIat,
      "organization subscription expired": isSubscriptionExpired,
      "organization subscription cancelled": !!account.cancelAt,
      "organization last payment date": account.lastSuccessfulPayment,
      "organization renewal date": account.nextPayment,
      "organization payment country": account.paymentCountry,
      "organization first subscription plan name": firstPlan.title,
      "organization first subscription plan period": firstPlan.period,
      "organization first subscription plan date": account.firstPaidAt,
      "organization first payment amount": account.firstPaymentAmount,
      "organization last payment amount": account.lastPaymentAmount,
      "organization lifetime payment amount": account.lifetimePaymentAmount,
      "days to end free trial":
        account.trialEnd && isFreeTrial
          ? moment(account.trialEnd).diff(moment(), "days") + 1
          : null,
      "organization subscription plan name":
        subscription && !isFreeTrial ? subscription.title : null,
      "organization subscription plan period":
        subscription && !isFreeTrial ? subscription.period : null,
      "organization timezone": account.hqTimezone,
      "react codebase": true,
    };

    const mappedParams = Object.entries(parameters).reduce(
      (acc, [key, value]) => {
        const parsedKey = key
          .replace(/([A-Z])/g, " $1")
          .toLowerCase()
          .trim();
        acc[parsedKey] = value;
        return acc;
      },
      {} as Record<string, unknown>,
    );

    Object.assign(data, mappedParams);
    register(data);
    setInitializedType("account");
  }, [
    authenticated,
    account,
    setInitializedType,
    isSubscriptionExpired,
    planType,
    subscription,
  ]);
};
